import { analytics } from "./analytics/posthog";
import { EditAnalytics } from "./analytics/edits";
import {
  Excalidraw,
  LiveCollaborationTrigger,
  TTDDialogTrigger,
  CaptureUpdateAction,
  reconcileElements,
  useEditorInterface,
  ExcalidrawAPIProvider,
  useExcalidrawAPI,
} from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "@excalidraw/excalidraw/components/CommandPalette/CommandPalette";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import { OverwriteConfirmDialog } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirm";
import { openConfirmModal } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import { Tooltip } from "@excalidraw/excalidraw/components/Tooltip";
import Trans from "@excalidraw/excalidraw/components/Trans";
import {
  APP_NAME,
  ARROW_TYPE,
  EVENT,
  VERSION_TIMEOUT,
  debounce,
  getVersion,
  getFrame,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  isRunningInIframe,
  isDevEnv,
} from "@excalidraw/common";
import polyfill from "@excalidraw/excalidraw/polyfill";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { t } from "@excalidraw/excalidraw/i18n";

import {
  GithubIcon,
  XBrandIcon,
  DiscordIcon,
  ExcalLogo,
  usersIcon,
  share,
  youtubeIcon,
} from "@excalidraw/excalidraw/components/icons";
import { isElementLink } from "@excalidraw/element";
import {
  bumpElementVersions,
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";
import { newElementWith } from "@excalidraw/element";
import { isInitializedImageElement } from "@excalidraw/element";
// DRAWOLA:CUSTOM-START SHOW_SHARE_LINK
import { getNonDeletedElements } from "@excalidraw/element";
// DRAWOLA:CUSTOM-END
import clsx from "clsx";
import {
  parseLibraryTokensFromUrl,
  useHandleLibrary,
} from "@excalidraw/excalidraw/data/library";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type { RestoredDataState } from "@excalidraw/excalidraw/data/restore";
import type {
  FileId,
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
  ExcalidrawProps,
} from "@excalidraw/excalidraw/types";
import type { ResolutionType } from "@excalidraw/common/utility-types";
import type { ResolvablePromise } from "@excalidraw/common/utils";

import CustomStats from "./CustomStats";
import {
  Provider,
  useAtom,
  useAtomValue,
  useAtomWithInitialValue,
  appJotaiStore,
} from "./app-jotai";
import {
  // DRAWOLA:CUSTOM-START SUPABASE_SHARE_FILES
  // Share images load from the fork's own backend, see ./data/shareStorage
  // DRAWOLA:CUSTOM-END
  isDrawolaPlusSignedUser,
  STORAGE_KEYS,
  SYNC_BROWSER_TABS_TIMEOUT,
} from "./app_constants";
import Collab, {
  collabAPIAtom,
  collabRoomAtom,
  collabSessionDocPendingAtom,
  isCollaboratingAtom,
  isOfflineAtom,
  userToFollowAtom,
} from "./collab/Collab";
import { buildCollabAvailabilityMessage } from "./collab/collabAvailabilityMessage";
import { CollabSessionTimer } from "./collab/CollabSessionTimer";
import { useCollabAvailability } from "./collab/useCollabAvailability";
// DRAWOLA:CUSTOM-START SHOW_LIVE_COLLAB session draw
import { useCollabSessionDoc } from "./collab/useCollabSessionDoc";
// DRAWOLA:CUSTOM-END
import { AppFooter } from "./components/AppFooter";
import { AppMainMenu } from "./components/AppMainMenu";
import { AppWelcomeScreen } from "./components/AppWelcomeScreen";
import { TopErrorBoundary } from "./components/TopErrorBoundary";

import {
  exportToBackend,
  getCollaborationLinkData,
  importFromBackend,
  isCollaborationLink,
} from "./data";

import { updateStaleImageStatuses } from "./data/FileManager";
import { FileStatusStore } from "./data/fileStatusStore";
import {
  importFromLocalStorage,
  importUsernameFromLocalStorage,
} from "./data/localStorage";

import { DRAWOLA_FEATURES } from "./drawola-features";
// DRAWOLA:CUSTOM-START SHOW_MINDMAP
import { DrawolaMindmapOverlay, insertRootMindmapBlock } from "./mindmap";
// DRAWOLA:CUSTOM-END
// DRAWOLA:CUSTOM-START SUPABASE_SHARE_FILES
// Share images load from the fork's own backend, see ./data/shareStorage
import { loadFilesFromShareStorage } from "./data/shareStorage";
// DRAWOLA:CUSTOM-END
import {
  LibraryIndexedDBAdapter,
  LibraryLocalStorageMigrationAdapter,
  LocalData,
  localStorageQuotaExceededAtom,
} from "./data/LocalData";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import { ShareDialog, shareDialogStateAtom } from "./share/ShareDialog";
import CollabError, { collabErrorIndicatorAtom } from "./collab/CollabError";
import { useHandleAppTheme } from "./useHandleAppTheme";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import DebugCanvas, {
  debugRenderer,
  isVisualDebuggerEnabled,
  loadSavedDebugState,
} from "./components/DebugCanvas";
import { useSimulatedCollaborators } from "./debugCollaborators";
import { AIComponents } from "./components/AI";
import { ExcalidrawPlusIframeExport } from "./ExcalidrawPlusIframeExport";

import "./index.scss";

import { ExcalidrawPlusPromoBanner } from "./components/ExcalidrawPlusPromoBanner";
import { AppSidebar } from "./components/AppSidebar";
import { Minimap } from "./components/Minimap";
import { DrawolaShapeLibrary } from "./components/shape-library/DrawolaShapeLibrary";

// DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
import { useMultiFileStore } from "./storage/useMultiFileStore";
import { BlockingQuotaModal } from "./storage/ui/BlockingQuotaModal";
import { drawolaT } from "./storage/i18n";
import {
  docNameFromExternalUrl,
  readExternalSceneLink,
} from "./studio/deepLinks";
import "./storage/ui/storage.scss";
// DRAWOLA:CUSTOM-END

// DRAWOLA:CUSTOM-START SHOW_CLOUD_STORAGE
import "./cloud/ui/cloud.scss";
// DRAWOLA:CUSTOM-END

// DRAWOLA:CUSTOM-START SHOW_CLOUD_STORAGE sync
import { CloudSyncEngine, SyncRepairPanel, useCloudSync } from "./cloud/sync";
import {
  getProvider as getCloudProvider,
  listProviders as listCloudProviders,
} from "./cloud/registry";
import { subscribeDocWritten } from "./storage/docEvents";
import { createDoc, listDocs, saveDoc } from "./storage/multiFileStore";
// DRAWOLA:CUSTOM-END

// DRAWOLA:CUSTOM-START SHOW_STUDIO_HOME
import {
  DrawolaStudio,
  EditorBreadcrumb,
  STUDIO_HOME_ICON,
  useStaticWelcomeCleanup,
  useStudioEditorIsolation,
  useStudioView,
} from "./studio";
import "./studio/studio.scss";
// DRAWOLA:CUSTOM-END

// DRAWOLA:CUSTOM-START ENABLE_IMAGE_IMPORT_LIMIT
import { ImageImportDialog } from "./image-import/ImageImportDialog";
import { useImageImportGuard } from "./image-import/useImageImportGuard";
// DRAWOLA:CUSTOM-END

// DRAWOLA:CUSTOM-START SHOW_SHARE_LINK
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { copyTextToSystemClipboard } from "@excalidraw/excalidraw/clipboard";

import { getDoc, getDocMeta, removeDocShare } from "./storage/multiFileStore";
import { DrawolaShareLinkDialog } from "./components/DrawolaShareLinkDialog";
// DRAWOLA:CUSTOM-START SUPABASE_SHARE_FILES
import { addDocShare } from "./storage/multiFileStore";
import { revokeShare } from "./data/shareStorage";
// DRAWOLA:CUSTOM-END
import { SharesListView } from "./studio/SharesListView";

import type { DocShare } from "./storage/types";
// DRAWOLA:CUSTOM-END

import type { CollabAPI } from "./collab/Collab";
// DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
import type { ExternalSceneLink } from "./studio/deepLinks";
import type { ImportedScene } from "./studio/importDoc";
// DRAWOLA:CUSTOM-END

// DRAWOLA:CUSTOM-START SHOW_CLOUD_STORAGE sync
import type { CloudProviderId, CloudStorageProvider } from "./cloud/types";
// DRAWOLA:CUSTOM-END

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    // prevent Chrome <= 67 from automatically showing the prompt
    event.preventDefault();
    // cache for later use
    pwaEvent = event;
  },
);

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (error) {
    // ignore
  }
}

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

// DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
/**
 * Whether the overwrite prompt above must be skipped for an incoming scene.
 *
 * The prompt tells the user the shared scene replaces their current content
 * and offers a backup first. With the multi-file store that is no longer true:
 * a `#json=` / `#url=` scene lands in a NEW doc and replaces nothing, so the
 * warning only talks the user out of a safe action.
 *
 * Skipping means always taking the accept path, and nothing the declined path
 * did is lost: the `focus` re-entry only existed to re-ask a prompt a hidden
 * tab never showed (upstream issue 1919), `roomLinkData` is already null on
 * both branches that can skip, and the accept path strips the hash too.
 */
const skipOverwritePrompt = DRAWOLA_FEATURES.ENABLE_MULTI_FILE_STORE;
// DRAWOLA:CUSTOM-END

// DRAWOLA:CUSTOM-START SHOW_LIVE_COLLAB session draw
/**
 * Whether the multi-file store must not write anything right now.
 *
 * True between a join resetting the canvas and `useCollabSessionDoc` opening
 * the draw that room's scene belongs in. Declared at module level so its
 * identity is stable and the store can call it on every write.
 */
const shouldHoldSavesForCollab = (): boolean =>
  appJotaiStore.get(collabSessionDocPendingAtom);
// DRAWOLA:CUSTOM-END

const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI;
}): Promise<
  {
    scene: ExcalidrawInitialDataState | null;
    // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
    /**
     * `true` only when an external scene was really fetched and kept.
     * `isExternalScene` cannot answer that: it stays `true` when the user
     * declines the overwrite prompt (`scene` is then the local one), and it is
     * never `true` for `#url=` at all.
     */
    drawolaExternalSceneLoaded?: boolean;
    // DRAWOLA:CUSTOM-END
  } & (
    | { isExternalScene: true; id: string; key: string }
    | { isExternalScene: false; id?: null; key?: null }
  )
> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const jsonBackendMatch = window.location.hash.match(
    /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
  );
  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  const localDataState = importFromLocalStorage();

  let scene: Omit<
    RestoredDataState,
    // we're not storing files in the scene database/localStorage, and instead
    // fetch them async from a different store
    "files"
  > & {
    scrollToContent?: boolean;
  } = {
    elements: restoreElements(localDataState?.elements, null, {
      repairBindings: true,
      deleteInvisibleElements: true,
    }),
    appState: restoreAppState(localDataState?.appState, null),
  };

  // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
  // See `drawolaExternalSceneLoaded` in the return type above.
  let externalSceneLoaded = false;
  // DRAWOLA:CUSTOM-END

  // DRAWOLA:CUSTOM-START SHOW_LIVE_COLLAB
  // With live collaboration off, a `#room=<id>,<key>` link must not start a
  // socket session against Excalidraw's own collab server (see
  // `startCollaboration` below), so treat it as absent here.
  // `getCollaborationLinkData` is skipped entirely, not just its result, so
  // it cannot pop its "invalid encryption key" alert for a link the app is
  // not going to honor anyway. The hash is stripped so the user does not
  // keep landing on a dead collab link on every reload.
  let roomLinkData = DRAWOLA_FEATURES.SHOW_LIVE_COLLAB
    ? getCollaborationLinkData(window.location.href)
    : null;
  if (
    !DRAWOLA_FEATURES.SHOW_LIVE_COLLAB &&
    isCollaborationLink(window.location.href)
  ) {
    window.history.replaceState({}, APP_NAME, window.location.origin);
  }
  // DRAWOLA:CUSTOM-END
  const isExternalScene = !!(id || jsonBackendMatch || roomLinkData);
  if (isExternalScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData ||
      // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
      // a `#json=` scene gets its own doc, so there is nothing to overwrite
      (skipOverwritePrompt && !!jsonBackendMatch) ||
      // DRAWOLA:CUSTOM-END
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (jsonBackendMatch) {
        const imported = await importFromBackend(
          jsonBackendMatch[1],
          jsonBackendMatch[2],
        );

        scene = {
          elements: bumpElementVersions(
            restoreElements(imported.elements, null, {
              repairBindings: true,
              deleteInvisibleElements: true,
            }),
            localDataState?.elements,
          ),
          appState: restoreAppState(
            imported.appState,
            // local appState when importing from backend to ensure we restore
            // localStorage user settings which we do not persist on server.
            localDataState?.appState,
          ),
        };
        // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
        externalSceneLoaded = true;
        // DRAWOLA:CUSTOM-END
      }
      scene.scrollToContent = true;
      if (!roomLinkData) {
        window.history.replaceState({}, APP_NAME, window.location.origin);
      }
    } else {
      // https://github.com/excalidraw/excalidraw/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      window.history.replaceState({}, APP_NAME, window.location.origin);
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, window.location.origin);

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
        // same as the `#json=` branch above: this scene gets its own doc
        skipOverwritePrompt ||
        // DRAWOLA:CUSTOM-END
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return {
          scene: data,
          isExternalScene,
          // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
          drawolaExternalSceneLoaded: true,
          // DRAWOLA:CUSTOM-END
        };
      }
    } catch (error: any) {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene,
      };
    }
  }

  if (roomLinkData && opts.collabAPI) {
    const { excalidrawAPI } = opts;

    const scene = await opts.collabAPI.startCollaboration(roomLinkData);

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...scene,
        appState: {
          ...restoreAppState(
            {
              ...scene?.appState,
              theme: localDataState?.appState?.theme || scene?.appState?.theme,
            },
            excalidrawAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
        },
        elements: reconcileElements(
          scene?.elements || [],
          excalidrawAPI.getSceneElementsIncludingDeleted() as RemoteExcalidrawElement[],
          excalidrawAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
    };
  } else if (scene) {
    return isExternalScene && jsonBackendMatch
      ? {
          scene,
          isExternalScene,
          id: jsonBackendMatch[1],
          key: jsonBackendMatch[2],
          // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
          drawolaExternalSceneLoaded: externalSceneLoaded,
          // DRAWOLA:CUSTOM-END
        }
      : { scene, isExternalScene: false };
  }
  return { scene: null, isExternalScene: false };
};

const ExcalidrawWrapper = () => {
  const excalidrawAPI = useExcalidrawAPI();

  const [errorMessage, setErrorMessage] = useState("");
  const isCollabDisabled = isRunningInIframe();

  const { editorTheme, appTheme, setAppTheme } = useHandleAppTheme();

  const [langCode, setLangCode] = useAppLangCode();

  const editorInterface = useEditorInterface();

  // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
  const multiFile = useMultiFileStore(
    excalidrawAPI,
    DRAWOLA_FEATURES.ENABLE_MULTI_FILE_STORE,
    // DRAWOLA:CUSTOM-START SHOW_LIVE_COLLAB session draw
    shouldHoldSavesForCollab,
    // DRAWOLA:CUSTOM-END
  );

  // DRAWOLA:CUSTOM-START SHOW_CLOUD_STORAGE sync
  // One engine for the app's life. It reaches the store hook through a ref
  // because the hook's return changes identity on every render.
  const multiFileRef = useRef(multiFile);
  multiFileRef.current = multiFile;
  const cloudSyncEngine = useMemo(() => {
    if (
      !DRAWOLA_FEATURES.SHOW_CLOUD_STORAGE ||
      !DRAWOLA_FEATURES.ENABLE_MULTI_FILE_STORE
    ) {
      return null;
    }
    const providers: Partial<Record<CloudProviderId, CloudStorageProvider>> =
      {};
    for (const id of listCloudProviders()) {
      const provider = getCloudProvider(id);
      if (provider) {
        providers[id] = provider;
      }
    }
    if (Object.keys(providers).length === 0) {
      return null;
    }
    return new CloudSyncEngine({
      providers,
      store: {
        getDoc,
        getDocMeta,
        listDocs,
        saveDoc,
        createDoc,
      },
      onDocsChanged: () => multiFileRef.current.notifyDocsChanged(),
      flushLocalSave: () => multiFileRef.current.flushSave(),
      reloadDoc: async (docId) => {
        const store = multiFileRef.current;
        if (store.activeDocId === docId) {
          // whatever the debounce still holds belongs to the copy that was
          // just replaced
          store.discardPendingSave();
          await store.switchTo(docId);
        }
      },
      subscribeDocWritten,
    });
  }, []);
  const cloudSync = useCloudSync(cloudSyncEngine);
  useEffect(() => {
    if (!cloudSyncEngine || !multiFile.booted) {
      return undefined;
    }
    void cloudSyncEngine.boot().then(() => {
      const active = multiFileRef.current.activeDocId;
      if (active) {
        void cloudSyncEngine.pullIfRemoteNewer(active);
      }
    });
    return () => cloudSyncEngine.dispose();
  }, [cloudSyncEngine, multiFile.booted]);
  // DRAWOLA:CUSTOM-END

  // DRAWOLA:CUSTOM-START ENABLE_IMAGE_IMPORT_LIMIT
  const imageImport = useImageImportGuard(
    DRAWOLA_FEATURES.ENABLE_IMAGE_IMPORT_LIMIT ? excalidrawAPI : null,
  );
  // DRAWOLA:CUSTOM-END

  // A `#json=` / `#url=` scene belongs in its own doc, never on top of the
  // draw the user had open.
  //
  // `link` is read at mount because `initializeScene()` strips the hash
  // before the scene resolves. It is single-use: StrictMode runs the boot
  // effect twice, and only the pass that takes the link may act on it.
  //
  // `claims` counts the imports still on their way to their own doc. While it
  // is above zero the debounced save is held back, otherwise the imported
  // content would be written to the previously active doc first. Whoever adds
  // a claim gives that same claim back, so one import settling can never
  // unblock another one that is still running.
  const externalImportRef = useRef<{
    link: ExternalSceneLink | null;
    claims: number;
  } | null>(null);
  if (!externalImportRef.current) {
    externalImportRef.current = {
      link: DRAWOLA_FEATURES.ENABLE_MULTI_FILE_STORE
        ? readExternalSceneLink()
        : null,
      claims: 0,
    };
  }
  const externalImport = externalImportRef.current;
  const [queuedExternalScene, setQueuedExternalScene] = useState<{
    name: string;
    scene: ImportedScene;
  } | null>(null);

  /**
   * Hand a freshly loaded external scene to the import effect below.
   *
   * Adds a claim of its own when it queues, so the guard stays closed while
   * the caller gives its own claim back. Never releases: releasing is always
   * the job of whoever claimed.
   *
   * @param link the link this caller took, `null` when it took none
   * @returns `true` when the scene was queued, meaning the caller must not
   *   push it into the editor itself: `importSceneAsNewDoc` does that.
   */
  const queueExternalSceneImport = useCallback(
    (
      link: ExternalSceneLink | null,
      data: ResolutionType<typeof initializeScene>,
    ): boolean => {
      if (!link) {
        return false;
      }
      if (!data.scene || !data.drawolaExternalSceneLoaded) {
        // nothing was imported (bad link, or the user declined the prompt)
        return false;
      }
      const fallbackName = drawolaT("files.untitledPrefix");
      externalImport.claims += 1;
      setQueuedExternalScene({
        name:
          link.kind === "url"
            ? docNameFromExternalUrl(link.url, fallbackName)
            : fallbackName,
        scene: {
          elements: (data.scene.elements ??
            []) as readonly OrderedExcalidrawElement[],
          appState: data.scene.appState ?? {},
          files: data.scene.files ?? {},
        },
      });
      return true;
    },
    [externalImport],
  );

  const {
    booted: multiFileBooted,
    importSceneAsNewDoc: importSceneAsNewMultiFileDoc,
  } = multiFile;

  // Wait for `booted` on purpose: the store's own boot runs migrations and
  // then primes the canvas from the active doc. Creating the new doc earlier
  // would race the migrations, and switching earlier would be undone by that
  // priming `updateScene`.
  useEffect(() => {
    if (!queuedExternalScene || !multiFileBooted) {
      return;
    }
    // drop the queue before awaiting so a re-render cannot start a second
    // import of the same scene
    setQueuedExternalScene(null);
    importSceneAsNewMultiFileDoc(
      queuedExternalScene.name,
      queuedExternalScene.scene,
    )
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error("[drawOla] importing an external scene failed", e);
      })
      .finally(() => {
        // give back the claim `queueExternalSceneImport` added for us
        externalImport.claims -= 1;
      });
  }, [
    queuedExternalScene,
    multiFileBooted,
    importSceneAsNewMultiFileDoc,
    externalImport,
  ]);
  // DRAWOLA:CUSTOM-END

  // DRAWOLA:CUSTOM-START SHOW_STUDIO_HOME
  // The Studio browses the multi-file store, so it needs both flags on.
  const studioEnabled =
    DRAWOLA_FEATURES.SHOW_STUDIO_HOME &&
    DRAWOLA_FEATURES.ENABLE_MULTI_FILE_STORE;
  const studio = useStudioView(studioEnabled);
  const isStudioOpen = studio.view === "studio";
  // DRAWOLA:CUSTOM-START seo welcome
  // The mounted Studio hands the static index.html welcome over itself; in
  // every other case nothing would, so drop it here.
  useStaticWelcomeCleanup(studioEnabled && isStudioOpen);
  // DRAWOLA:CUSTOM-END
  // The editor's own container, from its public onMount, so the hidden editor
  // can be kept out of Tab and key reach without a wrapper around it.
  const [editorContainer, setEditorContainer] = useState<HTMLElement | null>(
    null,
  );
  useStudioEditorIsolation(editorContainer, studioEnabled && isStudioOpen);
  const { flushSave: flushMultiFileSave } = multiFile;
  const { openStudio: showStudio, openEditor: showEditor } = studio;

  const handleGoHome = useCallback(async () => {
    // DRAWOLA:CUSTOM-START SHOW_LIVE_COLLAB session draw
    // Going home always leaves the room. The Studio swaps the scene the
    // session is sharing, so staying in it is not an option, and a user who
    // could not reach this button had no way out of a room at all. `false`
    // skips upstream's "this overwrites your local drawing" confirm, which is
    // about upstream's single stored scene and would only be in the way here;
    // it also clears the `#room=` hash, so a reload does not rejoin.
    // Read from the store rather than through a hook: `collabAPI` is only
    // bound further down, after the Studio's own callbacks, and this runs at
    // click time anyway.
    const collab = appJotaiStore.get(collabAPIAtom);
    if (collab?.isCollaborating()) {
      collab.stopCollaboration(false);
    }
    // DRAWOLA:CUSTOM-END
    // land on an up-to-date list: commit whatever the debounced save holds
    await flushMultiFileSave();
    showStudio();
  }, [flushMultiFileSave, showStudio]);
  // DRAWOLA:CUSTOM-END

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }

  const debugCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  const [, setShareDialogState] = useAtom(shareDialogStateAtom);
  const [collabAPI] = useAtom(collabAPIAtom);
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    // DRAWOLA:CUSTOM-START SHOW_LIVE_COLLAB
    // Mirrors the gate in `initializeScene()`: with the flag off,
    // `startCollaboration` is never called for a `#room=` link, so the UI
    // must not report itself as "collaborating" for one either.
    if (!DRAWOLA_FEATURES.SHOW_LIVE_COLLAB) {
      return false;
    }
    // DRAWOLA:CUSTOM-END
    return isCollaborationLink(window.location.href);
  });
  const collabError = useAtomValue(collabErrorIndicatorAtom);
  const userToFollow = useAtomValue(userToFollowAtom);
  // DRAWOLA:CUSTOM-START SHOW_LIVE_COLLAB session draw
  // Gives a joined room a draw of its own instead of writing its scene over
  // whichever draw the user had open, and honours the room's retain policy
  // when the session ends.
  const collabRoom = useAtomValue(collabRoomAtom);
  useCollabSessionDoc({
    enabled:
      DRAWOLA_FEATURES.ENABLE_MULTI_FILE_STORE &&
      DRAWOLA_FEATURES.SHOW_LIVE_COLLAB,
    room: collabRoom,
    store: multiFile,
    docName: drawolaT("collab.session.docName"),
  });
  // DRAWOLA:CUSTOM-END
  // DRAWOLA:CUSTOM-START SHOW_LIVE_COLLAB availability
  // Only ever fetches while `SHOW_LIVE_COLLAB` or `SHOW_SHARE_BUTTON` is on
  // (see `useCollabAvailability`'s own guard), so this is a no-op with both
  // flags at their default `false`.
  const collabAvailability = useCollabAvailability();
  // A session already in progress must stay usable regardless of current
  // quota (the user is managing/leaving THEIR OWN room, not starting a new
  // one), so the entry points only read this while not yet collaborating.
  const isCollabBlockedByQuota =
    !isCollaborating && !collabAvailability.allowed;
  const collabAvailabilityMessage = buildCollabAvailabilityMessage(
    collabAvailability,
    drawolaT,
  );
  // `null` means "show the entry point normally"; any other value is both
  // the reason to disable it and the message to show for why.
  const collabDisabledReason = isCollabBlockedByQuota
    ? collabAvailabilityMessage
    : null;
  // DRAWOLA:CUSTOM-END

  const viewportStatusFrame = useMemo(
    () =>
      userToFollow
        ? {
            border: "var(--color-primary-hover)",
            label: {
              label: (
                <>
                  Following{" "}
                  <span
                    style={{
                      display: "block",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 100,
                    }}
                    title={userToFollow.username}
                  >
                    {userToFollow.username}
                  </span>
                </>
              ),
              onClose: () => collabAPI?.setUserToFollow(null),
            },
          }
        : null,
    [userToFollow, collabAPI],
  );

  useHandleLibrary({
    excalidrawAPI,
    adapter: LibraryIndexedDBAdapter,
    // TODO maybe remove this in several months (shipped: 24-03-11)
    migrationAdapter: LibraryLocalStorageMigrationAdapter,
  });

  const [, forceRefresh] = useState(false);

  useEffect(() => {
    if (isDevEnv()) {
      const debugState = loadSavedDebugState();

      if (debugState.enabled && !window.visualDebug) {
        window.visualDebug = {
          data: [],
        };
      } else {
        delete window.visualDebug;
      }
      forceRefresh((prev) => !prev);
    }
  }, [excalidrawAPI]);

  // DRAWOLA:CUSTOM-START SHOW_MINDMAP
  useEffect(() => {
    if (!DRAWOLA_FEATURES.SHOW_MINDMAP || !excalidrawAPI) {
      return undefined;
    }
    const handler = () => insertRootMindmapBlock(excalidrawAPI);
    window.addEventListener("drawola:mindmap:create-root", handler);
    return () => {
      window.removeEventListener("drawola:mindmap:create-root", handler);
    };
  }, [excalidrawAPI]);
  // DRAWOLA:CUSTOM-END

  // ?collaborators=<N> — populate the canvas with N static fake
  // collaborators for exercising avatar/UserList UI without a real
  // collab room
  useSimulatedCollaborators(excalidrawAPI);

  // ---------------------------------------------------------------------------
  // Hoisted loadImages
  // ---------------------------------------------------------------------------
  const loadImages = useCallback(
    (data: ResolutionType<typeof initializeScene>, isInitialLoad = false) => {
      if (!data.scene || !excalidrawAPI) {
        return;
      }

      if (collabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          collabAPI
            .fetchImageFiles({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              excalidrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (data.isExternalScene) {
          if (fileIds.length) {
            // Direct share-storage call (not through FileManager), so track
            // manually
            FileStatusStore.updateStatuses(
              fileIds.map((id) => [id, "loading"]),
            );
          }
          // DRAWOLA:CUSTOM-START SUPABASE_SHARE_FILES
          loadFilesFromShareStorage(data.id, data.key, fileIds).then(
            ({ loadedFiles, erroredFiles }) => {
              excalidrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
              FileStatusStore.updateStatuses([
                ...loadedFiles.map(
                  (f) => [f.id, "loaded"] as [FileId, "loaded"],
                ),
                ...[...erroredFiles.keys()].map(
                  (id) => [id, "error"] as [FileId, "error"],
                ),
              ]);
            },
          );
          // DRAWOLA:CUSTOM-END
        } else if (isInitialLoad) {
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(async ({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          LocalData.fileStorage.clearObsoleteFiles({
            currentFileIds: fileIds,
          });
        }
      }
    },
    [collabAPI, excalidrawAPI],
  );

  useEffect(() => {
    if (!excalidrawAPI || (!isCollabDisabled && !collabAPI)) {
      return;
    }

    // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
    // Take the boot link here, before the first await, and take it only once:
    // StrictMode runs this effect twice and the second pass must not act on a
    // link the first pass already owns. Claiming the guard in the same
    // synchronous block keeps it closed for the whole round trip.
    const bootSceneLink = externalImport.link;
    externalImport.link = null;
    if (bootSceneLink) {
      externalImport.claims += 1;
    }
    // DRAWOLA:CUSTOM-END

    initializeScene({ collabAPI, excalidrawAPI })
      .then(async (data) => {
        loadImages(data, /* isInitialLoad */ true);
        // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
        // Still resolve with the external scene: the editor shows it (and
        // scrolls to it) right away, and the queued import re-applies the very
        // same elements once the store is booted.
        queueExternalSceneImport(bootSceneLink, data);
        // DRAWOLA:CUSTOM-END
        initialStatePromiseRef.current.promise.resolve(data.scene);
      })
      // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
      // Upstream leaves this chain uncaught, where a rejection only means
      // "the shared scene did not load". Here it would also leave the guard
      // claimed for good and silently kill every later save, so the claim is
      // given back on every path, success or failure.
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error("[drawOla] loading the initial scene failed", e);
      })
      .finally(() => {
        if (bootSceneLink) {
          externalImport.claims -= 1;
        }
      });
    // DRAWOLA:CUSTOM-END

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
        // Same trap as on boot, and reachable at any time: pasting a `#json=`
        // link into the address bar of the running app re-enters
        // `initializeScene()`. Read the hash before that strips it, and claim
        // the guard from here on.
        const hashSceneLink = DRAWOLA_FEATURES.ENABLE_MULTI_FILE_STORE
          ? readExternalSceneLink()
          : null;
        if (hashSceneLink) {
          externalImport.claims += 1;
        }
        // DRAWOLA:CUSTOM-END
        if (
          collabAPI?.isCollaborating() &&
          !isCollaborationLink(window.location.href)
        ) {
          collabAPI.stopCollaboration(false);
        }
        excalidrawAPI.updateScene({ appState: { isLoading: true } });

        initializeScene({ collabAPI, excalidrawAPI })
          .then((data) => {
            loadImages(data);
            // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
            if (queueExternalSceneImport(hashSceneLink, data)) {
              // the import swaps the scene itself, via `switchTo`
              excalidrawAPI.updateScene({ appState: { isLoading: false } });
              return;
            }
            // DRAWOLA:CUSTOM-END
            if (data.scene) {
              excalidrawAPI.updateScene({
                elements: restoreElements(data.scene.elements, null, {
                  repairBindings: true,
                }),
                appState: restoreAppState(data.scene.appState, null),
                captureUpdate: CaptureUpdateAction.IMMEDIATELY,
              });
            }
          })
          // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
          // A shared link that fails to load must never stop the user's own
          // draws from saving, so the claim goes back on every path. The
          // editor is already up here, which is exactly when a stuck guard
          // would cost real work.
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.error(
              "[drawOla] loading the scene from the hash failed",
              e,
            );
          })
          .finally(() => {
            if (hashSceneLink) {
              externalImport.claims -= 1;
            }
          });
        // DRAWOLA:CUSTOM-END
      }
    };

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }
      if (
        !document.hidden &&
        ((collabAPI && !collabAPI.isCollaborating()) || isCollabDisabled)
      ) {
        // don't sync if local state is newer or identical to browser state
        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_DATA_STATE)) {
          const localDataState = importFromLocalStorage();
          const username = importUsernameFromLocalStorage();
          setLangCode(getPreferredLanguage());
          // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
          // localStorage keeps one global scene while this store keeps one per
          // draw. Syncing can put another draw here, then the debounced save
          // writes those elements into this draw.
          if (!DRAWOLA_FEATURES.ENABLE_MULTI_FILE_STORE) {
            excalidrawAPI.updateScene({
              ...localDataState,
              captureUpdate: CaptureUpdateAction.NEVER,
            });
          }
          // DRAWOLA:CUSTOM-END
          LibraryIndexedDBAdapter.load().then((data) => {
            if (data) {
              excalidrawAPI.updateLibrary({
                libraryItems: data.libraryItems,
              });
            }
          });
          collabAPI?.setUsername(username || "");
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = excalidrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
    };
  }, [
    isCollabDisabled,
    collabAPI,
    excalidrawAPI,
    setLangCode,
    loadImages,
    // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
    queueExternalSceneImport,
    externalImport,
    // DRAWOLA:CUSTOM-END
  ]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();

      if (
        excalidrawAPI &&
        LocalData.fileStorage.shouldPreventUnload(
          excalidrawAPI.getSceneElements(),
        )
      ) {
        if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
          preventUnload(event);
        } else {
          console.warn(
            "preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)",
          );
        }
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [excalidrawAPI]);

  // DRAWOLA:CUSTOM-START close-menu-on-tool-pick
  const lastActiveToolTypeRef = useRef<AppState["activeTool"]["type"] | null>(
    null,
  );
  // DRAWOLA:CUSTOM-END

  const [editAnalytics] = useState(
    () => new EditAnalytics((tool) => analytics.used(tool)),
  );

  const onChange = (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    if (collabAPI?.isCollaborating()) {
      collabAPI.syncElements(elements);
    }

    // DRAWOLA:CUSTOM-START force-elbow-arrows
    // Drawola is elbow-only: every new arrow routes around obstacles and the
    // sharp / round / elbow chooser is hidden (see index.scss). The editor
    // still owns `currentItemArrowType` — it is restored from localStorage,
    // carried on a saved draw and cycled by pressing the arrow-tool shortcut
    // twice — so pin it back here. Keeping it in the app means the library
    // keeps upstream's own "new arrows follow the selected arrow type" rule.
    if (appState.currentItemArrowType !== ARROW_TYPE.elbow) {
      excalidrawAPI?.updateScene({
        appState: { currentItemArrowType: ARROW_TYPE.elbow },
        captureUpdate: CaptureUpdateAction.NEVER,
      });
    }
    // DRAWOLA:CUSTOM-END

    // DRAWOLA:CUSTOM-START close-menu-on-tool-pick
    // The main menu's own outside-click handler never attaches (its content
    // mounts while closed, so the hook sees a null ref), and upstream only
    // closes the menu on a canvas pointerdown. Picking a tool from the toolbar
    // or by shortcut therefore left the menu open on top of the tool's
    // properties island. Treat a tool change as leaving the menu.
    if (
      appState.openMenu === "canvas" &&
      lastActiveToolTypeRef.current !== null &&
      lastActiveToolTypeRef.current !== appState.activeTool.type
    ) {
      excalidrawAPI?.updateScene({
        appState: { openMenu: null },
        captureUpdate: CaptureUpdateAction.NEVER,
      });
    }
    lastActiveToolTypeRef.current = appState.activeTool.type;
    // DRAWOLA:CUSTOM-END

    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time
    if (!LocalData.isSavePaused()) {
      LocalData.save(elements, appState, files, () => {
        if (excalidrawAPI) {
          let didChange = false;

          const elements = excalidrawAPI
            .getSceneElementsIncludingDeleted()
            .map((element) => {
              if (
                LocalData.fileStorage.shouldUpdateImageElementStatus(element)
              ) {
                const newElement = newElementWith(element, { status: "saved" });
                if (newElement !== element) {
                  didChange = true;
                }
                return newElement;
              }
              return element;
            });

          if (didChange) {
            excalidrawAPI.updateScene({
              elements,
              captureUpdate: CaptureUpdateAction.NEVER,
            });
          }
        }
      });
    }

    // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
    // An open claim means an incoming `#json=` / `#url=` scene is still on its
    // way to its own doc; until then nothing may be written to whichever doc
    // happens to be active.
    // The editor stays mounted under the Studio and upstream fires onChange on
    // every render, so a hidden editor would save its untouched default state
    // and turn a fresh starter draw into "used" content. Only a visible editor
    // queues a save. A live room is the exception: incoming room scenes reach
    // local storage only through this onChange. Saves queued before the
    // Studio opened are left alone and still flush.
    if (
      DRAWOLA_FEATURES.ENABLE_MULTI_FILE_STORE &&
      multiFile.booted &&
      externalImport.claims === 0 &&
      (!isStudioOpen || !!collabAPI?.isCollaborating())
    ) {
      multiFile.scheduleSave(elements, appState, files);
    }
    // DRAWOLA:CUSTOM-END

    // Render the debug scene if the debug canvas is available
    if (debugCanvasRef.current && excalidrawAPI) {
      debugRenderer(
        debugCanvasRef.current,
        appState,
        elements,
        window.devicePixelRatio,
      );
    }
  };

  // DRAWOLA:CUSTOM-START SHOW_SHARE_LINK
  const [latestShareableLink, setLatestShareableLink] = useState<{
    url: string;
    expiresAt?: number;
  } | null>(null);
  // DRAWOLA:CUSTOM-END

  // DRAWOLA:CUSTOM-START SHOW_SHARE_LINK
  // State for the manage-links dialog opened from the main menu. The shares
  // list is re-read from the store when the dialog opens and after each
  // revoke, so it never shows a row that is already gone.
  const [manageSharesOpen, setManageSharesOpen] = useState(false);
  const [activeDocShares, setActiveDocShares] = useState<DocShare[] | null>(
    null,
  );
  const [busyShareId, setBusyShareId] = useState<string | null>(null);
  const [copiedShareId, setCopiedShareId] = useState<string | null>(null);
  const [sharesError, setSharesError] = useState<string | null>(null);
  const copyTimerRef = useRef<number>(0);

  useEffect(() => {
    return () => window.clearTimeout(copyTimerRef.current);
  }, []);
  // DRAWOLA:CUSTOM-END

  const onExportToBackend = async (
    exportedElements: readonly NonDeletedExcalidrawElement[],
    appState: Partial<AppState>,
    files: BinaryFiles,
    // DRAWOLA:CUSTOM-START SUPABASE_SHARE_FILES
    docId?: string,
    // DRAWOLA:CUSTOM-END
  ) => {
    if (exportedElements.length === 0) {
      throw new Error(t("alerts.cannotExportEmptyCanvas"));
    }
    try {
      // DRAWOLA:CUSTOM-START SUPABASE_SHARE_FILES
      const { url, errorMessage, share } = await exportToBackend(
        exportedElements,
        {
          ...appState,
          viewBackgroundColor: appState.exportBackground
            ? appState.viewBackgroundColor
            : getDefaultAppState().viewBackgroundColor,
        },
        files,
      );
      // DRAWOLA:CUSTOM-END

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      // DRAWOLA:CUSTOM-START SHOW_SHARE_LINK
      if (url) {
        setLatestShareableLink({ url, expiresAt: share?.expiresAt });
      }
      // DRAWOLA:CUSTOM-END

      // DRAWOLA:CUSTOM-START SUPABASE_SHARE_FILES
      // Remember the share on the draw that produced it, so a later step can
      // revoke it. The Studio passes the doc id it shared; the editor shares
      // the open draw, so the active doc id is used there.
      if (share && DRAWOLA_FEATURES.ENABLE_MULTI_FILE_STORE) {
        const targetDocId = docId ?? multiFile.activeDocId;
        if (targetDocId) {
          await addDocShare(targetDocId, share);
          multiFile.notifyDocsChanged();
        }
      }
      // DRAWOLA:CUSTOM-END
    } catch (error: any) {
      if (error.name !== "AbortError") {
        const { width, height } = appState;
        console.error(error, {
          width,
          height,
          devicePixelRatio: window.devicePixelRatio,
        });
        throw new Error(error.message);
      }
    }
  };

  // DRAWOLA:CUSTOM-START SHOW_SHARE_LINK
  // Both handlers below funnel into `onExportToBackend` above, so the link
  // dialog and the error copy exist once.
  //
  // DRAWOLA:CUSTOM-START SUPABASE_SHARE_FILES
  // Scene and images both go to the fork's own share backend. The scene is
  // one object under the share id, the images live under a separate key
  // prefix, see data/index.ts `saveFilesToShareStorage`.
  // DRAWOLA:CUSTOM-END

  /** Share the scene the editor currently has open. */
  const handleShareCurrentScene = async () => {
    if (!excalidrawAPI) {
      return;
    }
    try {
      await onExportToBackend(
        excalidrawAPI.getSceneElements(),
        excalidrawAPI.getAppState(),
        excalidrawAPI.getFiles(),
      );
    } catch (error: any) {
      setErrorMessage(error.message);
    }
  };

  /** Share a stored draw picked from the Studio, without opening it. */
  const handleShareDoc = async (docId: string) => {
    // Reads the doc as stored, so the debounce must not still be holding an
    // edit. Reaching the Studio through the browser Back button skips
    // `handleGoHome`, which is the one path that would have flushed already.
    await flushMultiFileSave();
    const doc = await getDoc(docId);
    if (!doc) {
      return;
    }
    try {
      await onExportToBackend(
        // stored scenes keep their tombstones; an export must not carry them
        getNonDeletedElements(doc.elements),
        doc.appState,
        doc.files,
        // DRAWOLA:CUSTOM-START SUPABASE_SHARE_FILES
        // The Studio knows which draw it is sharing. Without this the share
        // would be recorded against whichever draw is open.
        docId,
        // DRAWOLA:CUSTOM-END
      );
    } catch (error: any) {
      setErrorMessage(error.message);
    }
  };

  /**
   * Revoke one shareable link.
   *
   * Returns `true` when the share is gone from the server and the local
   * record, `false` when the revoke failed. `silent` suppresses the app's
   * error dialog: the Studio's delete flow reports its own summary, so a
   * per-share dialog would be noise on top of it.
   */
  const handleRevokeShare = async (
    docId: string,
    shareId: string,
    silent = false,
  ): Promise<boolean> => {
    const doc = await getDocMeta(docId);
    const share = doc?.shares?.find((s) => s.id === shareId);
    if (!share) {
      return false;
    }
    try {
      const result = await revokeShare({
        shareId,
        secret: share.revokeSecret,
      });
      if (result === "revoked" || result === "gone") {
        // "gone" means the server already deleted the share, most likely
        // because it expired. That is a success from the user's point of
        // view, so the local record must go too.
        await removeDocShare(docId, shareId);
        multiFile.notifyDocsChanged();
        return true;
      }
      // "denied": the secret no longer authorises this share
      if (!silent) {
        setErrorMessage(drawolaT("shares.revokeFailed"));
      }
      return false;
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error("[drawOla] revoking a share failed", error);
      if (!silent) {
        setErrorMessage(drawolaT("shares.revokeFailed"));
      }
      return false;
    }
  };

  /** Open the manage-links dialog for the draw currently open in the editor. */
  const handleManageShares = useCallback(async () => {
    const docId = multiFile.activeDocId;
    if (!docId) {
      return;
    }
    const meta = await getDocMeta(docId);
    setActiveDocShares(meta?.shares ?? []);
    setSharesError(null);
    setManageSharesOpen(true);
  }, [multiFile.activeDocId]);

  /** Copy a share's link, the same way `StudioSharesDialog` builds it. */
  const handleCopyShare = useCallback(async (share: DocShare) => {
    const link = `${window.location.origin}${window.location.pathname}#json=${share.id},${share.key}`;
    try {
      await copyTextToSystemClipboard(link);
    } catch {
      setSharesError(drawolaT("shares.copyFailed"));
      return;
    }
    setSharesError(null);
    setCopiedShareId(share.id);
    window.clearTimeout(copyTimerRef.current);
    copyTimerRef.current = window.setTimeout(() => {
      setCopiedShareId(null);
    }, 2000);
  }, []);

  /** Revoke a share from the manage-links dialog, then drop its row. */
  const handleRevokeFromDialog = useCallback(
    async (share: DocShare) => {
      const docId = multiFile.activeDocId;
      if (!docId) {
        return;
      }
      setBusyShareId(share.id);
      try {
        const ok = await handleRevokeShare(docId, share.id);
        if (ok) {
          // the share is gone from the doc; re-read so the list drops the row
          const meta = await getDocMeta(docId);
          setActiveDocShares(meta?.shares ?? []);
        }
      } finally {
        setBusyShareId(null);
      }
    },
    [multiFile.activeDocId, handleRevokeShare],
  );
  // DRAWOLA:CUSTOM-END

  const renderCustomStats = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => excalidrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const localStorageQuotaExceeded = useAtomValue(localStorageQuotaExceededAtom);

  const onCollabDialogOpen = useCallback(
    () => setShareDialogState({ isOpen: true, type: "collaborationOnly" }),
    [setShareDialogState],
  );

  // ---------------------------------------------------------------------------
  // onExport — intercepts file save to wait for pending image loads
  // ---------------------------------------------------------------------------
  const onExport: Required<ExcalidrawProps>["onExport"] = useCallback(
    async function* () {
      let snapshot = FileStatusStore.getSnapshot();
      const { pending, total } = FileStatusStore.getPendingCount(
        snapshot.value,
      );
      if (pending === 0) {
        return;
      }

      // Yield initial progress
      yield {
        type: "progress",
        progress: (total - pending) / total,
        message: `Loading images (${total - pending}/${total})...`,
      };

      // Wait for all pending images to finish
      while (true) {
        snapshot = await FileStatusStore.pull(snapshot.version);
        const { pending: nowPending, total: nowTotal } =
          FileStatusStore.getPendingCount(snapshot.value);

        yield {
          type: "progress",
          progress: (nowTotal - nowPending) / nowTotal,
          message: `Loading images (${nowTotal - nowPending}/${nowTotal})...`,
        };

        if (nowPending === 0) {
          await new Promise((r) => setTimeout(r, 500));
          yield {
            type: "progress",
            message: `Preparing export...`,
          };
          return;
        }
      }
    },
    [],
  );

  // const onExport = () => {
  //   return new Promise((r) => setTimeout(r, 2500));
  //   // console.log("onExport");
  // };

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  const ExcalidrawPlusCommand = {
    label: "drawOla+",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: ["plus", "cloud", "server"],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_LP
        }/plus?utm_source=drawola&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };
  const ExcalidrawPlusAppCommand = {
    label: "Sign up",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: [
      "drawOla",
      "plus",
      "cloud",
      "server",
      "signin",
      "login",
      "signup",
    ],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_APP
        }?utm_source=drawola&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };

  return (
    <div
      onPointerDownCapture={() => editAnalytics.clearIntent()}
      onKeyDownCapture={() => editAnalytics.clearIntent()}
      style={{ height: "100%" }}
      className={clsx("excalidraw-app", {
        "is-collaborating": isCollaborating,
        // DRAWOLA:CUSTOM-START SHOW_LIBRARY
        "drawOla-hide-library": !DRAWOLA_FEATURES.SHOW_LIBRARY,
        // DRAWOLA:CUSTOM-END
        // DRAWOLA:CUSTOM-START FLOAT_LIBRARY
        "drawOla-float-library": DRAWOLA_FEATURES.FLOAT_LIBRARY,
        // DRAWOLA:CUSTOM-END
        // DRAWOLA:CUSTOM-START SHOW_MINDMAP
        "drawOla-hide-mindmap": !DRAWOLA_FEATURES.SHOW_MINDMAP,
        // DRAWOLA:CUSTOM-END
        // DRAWOLA:CUSTOM-START SHOW_MERMAID_IMPORT
        "drawOla-hide-mermaid-import": !DRAWOLA_FEATURES.SHOW_MERMAID_IMPORT,
        // DRAWOLA:CUSTOM-END
        // DRAWOLA:CUSTOM-START SHOW_STUDIO_HOME
        // CSS gate: the editor stays mounted (so useMultiFileStore keeps its
        // lifecycle) but is hidden behind the Studio overlay. See studio.scss.
        "drawola-studio-open": isStudioOpen,
        // DRAWOLA:CUSTOM-END
      })}
    >
      <Excalidraw
        viewportStatusFrame={viewportStatusFrame}
        userToFollow={userToFollow}
        onChange={onChange}
        onIncrement={(event) => editAnalytics.capture(event)}
        onPaste={() => {
          editAnalytics.intent("paste");
          return true;
        }}
        onHistoryAction={(action) => editAnalytics.intent(action)}
        onExportOperation={(format) => {
          const attempt = analytics.start(`export_${format}`);
          return (outcome) => {
            if (outcome === "succeeded") {
              attempt.succeed(
                format === "clipboard"
                  ? "clipboard_written"
                  : "download_handoff",
              );
            } else if (outcome === "cancelled") {
              attempt.cancel();
            } else {
              attempt.fail(undefined);
            }
          };
        }}
        onExport={onExport}
        initialData={initialStatePromiseRef.current.promise}
        isCollaborating={isCollaborating}
        onPointerDown={(tool) => editAnalytics.beginGesture(tool.type)}
        onPointerUpdate={collabAPI?.onPointerUpdate}
        aiEnabled={DRAWOLA_FEATURES.SHOW_AI_TOOLS}
        // DRAWOLA:CUSTOM-START ENABLE_IMAGE_IMPORT_LIMIT
        {...(DRAWOLA_FEATURES.ENABLE_IMAGE_IMPORT_LIMIT
          ? { generateIdForFile: imageImport.generateIdForFile }
          : {})}
        // DRAWOLA:CUSTOM-END
        UIOptions={{
          canvasActions: {
            toggleTheme: true,
            export: {
              onExportToBackend,
            },
          },
        }}
        langCode={langCode}
        renderCustomStats={renderCustomStats}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        // DRAWOLA:CUSTOM-START SHOW_STUDIO_HOME
        onMount={({ container }) => setEditorContainer(container)}
        // DRAWOLA:CUSTOM-END
        theme={editorTheme}
        onThemeChange={setAppTheme}
        renderTopRightUI={(isMobile) => {
          if (isMobile || !collabAPI || isCollabDisabled) {
            return null;
          }

          return (
            <div className="excalidraw-ui-top-right">
              {/* DRAWOLA:CUSTOM-START SHOW_PLUS_PROMO_BANNER */}
              {DRAWOLA_FEATURES.SHOW_PLUS_PROMO_BANNER &&
                excalidrawAPI?.getEditorInterface().formFactor ===
                  "desktop" && (
                  <ExcalidrawPlusPromoBanner
                    isSignedIn={isDrawolaPlusSignedUser}
                  />
                )}
              {/* DRAWOLA:CUSTOM-END */}

              {collabError.message && <CollabError collabError={collabError} />}
              {/* DRAWOLA:CUSTOM-START SHOW_LIVE_COLLAB availability */}
              <CollabSessionTimer />
              {/* DRAWOLA:CUSTOM-END */}
              {/* DRAWOLA:CUSTOM-START SHOW_SHARE_BUTTON */}
              {DRAWOLA_FEATURES.SHOW_SHARE_BUTTON &&
                (isCollabBlockedByQuota ? (
                  <Tooltip label={collabAvailabilityMessage ?? ""} long>
                    <LiveCollaborationTrigger
                      isCollaborating={isCollaborating}
                      onSelect={() => {}}
                      editorInterface={editorInterface}
                      disabled
                      aria-disabled="true"
                    />
                  </Tooltip>
                ) : (
                  <LiveCollaborationTrigger
                    isCollaborating={isCollaborating}
                    onSelect={() =>
                      setShareDialogState({ isOpen: true, type: "share" })
                    }
                    editorInterface={editorInterface}
                  />
                ))}
              {/* DRAWOLA:CUSTOM-END */}
            </div>
          );
        }}
        onLinkOpen={(element, event) => {
          if (element.link && isElementLink(element.link)) {
            event.preventDefault();
            excalidrawAPI?.setViewport({
              target: element.link,
              fit: "scale-down",
              animation: true,
            });
          }
        }}
      >
        <AppMainMenu
          onCollabDialogOpen={onCollabDialogOpen}
          isCollaborating={isCollaborating}
          isCollabEnabled={!isCollabDisabled}
          // DRAWOLA:CUSTOM-START SHOW_LIVE_COLLAB availability
          collabDisabledReason={collabDisabledReason}
          // DRAWOLA:CUSTOM-END
          theme={appTheme}
          refresh={() => forceRefresh((prev) => !prev)}
          // DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE
          multiFile={
            DRAWOLA_FEATURES.ENABLE_MULTI_FILE_STORE ? multiFile : undefined
          }
          // DRAWOLA:CUSTOM-END
          // DRAWOLA:CUSTOM-START SHOW_CLOUD_STORAGE
          excalidrawAPI={excalidrawAPI}
          // DRAWOLA:CUSTOM-END
          // DRAWOLA:CUSTOM-START SHOW_CLOUD_STORAGE sync
          cloudSync={cloudSync ?? undefined}
          // DRAWOLA:CUSTOM-END
          // DRAWOLA:CUSTOM-START SHOW_SHARE_LINK
          onShareLink={
            DRAWOLA_FEATURES.SHOW_SHARE_LINK
              ? handleShareCurrentScene
              : undefined
          }
          onManageShares={
            DRAWOLA_FEATURES.SHOW_SHARE_LINK ? handleManageShares : undefined
          }
          // DRAWOLA:CUSTOM-END
        />
        <AppWelcomeScreen
          onCollabDialogOpen={onCollabDialogOpen}
          isCollabEnabled={!isCollabDisabled}
          // DRAWOLA:CUSTOM-START SHOW_LIVE_COLLAB availability
          collabDisabledReason={collabDisabledReason}
          // DRAWOLA:CUSTOM-END
        />
        <OverwriteConfirmDialog>
          <OverwriteConfirmDialog.Actions.ExportToImage />
          <OverwriteConfirmDialog.Actions.SaveToDisk />
        </OverwriteConfirmDialog>
        <AppFooter onChange={() => excalidrawAPI?.refresh()} />
        {excalidrawAPI && <AIComponents excalidrawAPI={excalidrawAPI} />}

        <TTDDialogTrigger />
        {isCollaborating && isOffline && (
          <div className="alertalert--warning">
            {t("alerts.collabOfflineWarning")}
          </div>
        )}
        {localStorageQuotaExceeded && (
          <div className="alert alert--danger">
            {t("alerts.localStorageQuotaExceeded")}
          </div>
        )}
        {/* DRAWOLA:CUSTOM-START SHOW_SHARE_LINK */}
        {latestShareableLink && (
          <DrawolaShareLinkDialog
            link={latestShareableLink.url}
            expiresAt={latestShareableLink.expiresAt}
            onCloseRequest={() => setLatestShareableLink(null)}
            setErrorMessage={setErrorMessage}
          />
        )}
        {manageSharesOpen && (
          <Dialog
            onCloseRequest={() => setManageSharesOpen(false)}
            title={drawolaT("shares.title")}
            size="small"
          >
            {sharesError && (
              <div className="drawola-studio__error" role="alert">
                <span>{sharesError}</span>
              </div>
            )}
            <SharesListView
              shares={activeDocShares ?? []}
              t={drawolaT}
              onCopy={handleCopyShare}
              onRevoke={handleRevokeFromDialog}
              busyShareId={busyShareId}
              copiedShareId={copiedShareId}
            />
          </Dialog>
        )}
        {/* DRAWOLA:CUSTOM-END */}
        {/* DRAWOLA:CUSTOM-START ENABLE_IMAGE_IMPORT_LIMIT */}
        {DRAWOLA_FEATURES.ENABLE_IMAGE_IMPORT_LIMIT && (
          <ImageImportDialog
            state={imageImport.state}
            onAccept={imageImport.accept}
            onCancel={imageImport.cancel}
            onRecompress={imageImport.recompress}
          />
        )}
        {/* DRAWOLA:CUSTOM-END */}
        {excalidrawAPI && !isCollabDisabled && (
          <Collab excalidrawAPI={excalidrawAPI} />
        )}

        <ShareDialog
          collabAPI={collabAPI}
          onExportToBackend={async () => {
            if (excalidrawAPI) {
              try {
                await onExportToBackend(
                  excalidrawAPI.getSceneElements(),
                  excalidrawAPI.getAppState(),
                  excalidrawAPI.getFiles(),
                );
              } catch (error: any) {
                setErrorMessage(error.message);
              }
            }
          }}
        />

        <AppSidebar />

        {errorMessage && (
          <ErrorDialog onClose={() => setErrorMessage("")}>
            {errorMessage}
          </ErrorDialog>
        )}

        <CommandPalette
          customCommandPaletteItems={[
            {
              label: t("labels.liveCollaboration"),
              category: DEFAULT_CATEGORIES.app,
              keywords: [
                "team",
                "multiplayer",
                "share",
                "public",
                "session",
                "invite",
              ],
              icon: usersIcon,
              perform: () => {
                setShareDialogState({
                  isOpen: true,
                  type: "collaborationOnly",
                });
              },
            },
            {
              label: t("roomDialog.button_stopSession"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!collabAPI?.isCollaborating(),
              keywords: [
                "stop",
                "session",
                "end",
                "leave",
                "close",
                "exit",
                "collaboration",
              ],
              perform: () => {
                if (collabAPI) {
                  collabAPI.stopCollaboration();
                  if (!collabAPI.isCollaborating()) {
                    setShareDialogState({ isOpen: false });
                  }
                }
              },
            },
            {
              label: t("labels.share"),
              category: DEFAULT_CATEGORIES.app,
              predicate: true,
              icon: share,
              keywords: [
                "link",
                "shareable",
                "readonly",
                "export",
                "publish",
                "snapshot",
                "url",
                "collaborate",
                "invite",
              ],
              perform: async () => {
                setShareDialogState({ isOpen: true, type: "share" });
              },
            },
            // DRAWOLA:CUSTOM-START SHOW_STUDIO_HOME (command-palette Back to Drawola Studio)
            // Offered while collaborating too, like the main-menu entry and the
            // breadcrumb's home button: `handleGoHome` leaves the room first.
            // No `shortcut` is declared: the field is display-only
            // (CommandPalette renders it, it binds nothing), so advertising a
            // key combo here would be a lie, and a real binding would mean a
            // global keydown listener, which we deliberately avoid.
            ...(studioEnabled
              ? [
                  {
                    label: drawolaT("studio.breadcrumb.home"),
                    category: DEFAULT_CATEGORIES.app,
                    icon: STUDIO_HOME_ICON,
                    predicate: true,
                    keywords: [
                      "studio",
                      "home",
                      "back",
                      "files",
                      "drawings",
                      "library",
                      "browse",
                      "dashboard",
                    ],
                    perform: () => {
                      void handleGoHome();
                    },
                  },
                ]
              : []),
            // DRAWOLA:CUSTOM-END
            // DRAWOLA:CUSTOM-START SHOW_SOCIALS (GitHub + Follow us + Discord + YouTube)
            ...(DRAWOLA_FEATURES.SHOW_SOCIALS
              ? [
                  {
                    label: "GitHub",
                    icon: GithubIcon,
                    category: DEFAULT_CATEGORIES.links,
                    predicate: true,
                    keywords: [
                      "issues",
                      "bugs",
                      "requests",
                      "report",
                      "features",
                      "social",
                      "community",
                    ],
                    perform: () => {
                      window.open(
                        "https://github.com/excalidraw/excalidraw",
                        "_blank",
                        "noopener noreferrer",
                      );
                    },
                  },
                  {
                    label: t("labels.followUs"),
                    icon: XBrandIcon,
                    category: DEFAULT_CATEGORIES.links,
                    predicate: true,
                    keywords: ["twitter", "contact", "social", "community"],
                    perform: () => {
                      window.open(
                        "https://x.com/excalidraw",
                        "_blank",
                        "noopener noreferrer",
                      );
                    },
                  },
                  {
                    label: t("labels.discordChat"),
                    category: DEFAULT_CATEGORIES.links,
                    predicate: true,
                    icon: DiscordIcon,
                    keywords: [
                      "chat",
                      "talk",
                      "contact",
                      "bugs",
                      "requests",
                      "report",
                      "feedback",
                      "suggestions",
                      "social",
                      "community",
                    ],
                    perform: () => {
                      window.open(
                        "https://discord.gg/UexuTaE",
                        "_blank",
                        "noopener noreferrer",
                      );
                    },
                  },
                  {
                    label: "YouTube",
                    icon: youtubeIcon,
                    category: DEFAULT_CATEGORIES.links,
                    predicate: true,
                    keywords: [
                      "features",
                      "tutorials",
                      "howto",
                      "help",
                      "community",
                    ],
                    perform: () => {
                      window.open(
                        "https://youtube.com/@excalidraw",
                        "_blank",
                        "noopener noreferrer",
                      );
                    },
                  },
                ]
              : []),
            // DRAWOLA:CUSTOM-END
            // DRAWOLA:CUSTOM-START SHOW_DRAWOLA_PLUS + SHOW_SIGN_UP (command-palette links)
            ...(isDrawolaPlusSignedUser
              ? DRAWOLA_FEATURES.SHOW_SIGN_UP
                ? [
                    {
                      ...ExcalidrawPlusAppCommand,
                      label: "Sign in / Go to drawOla+",
                    },
                  ]
                : []
              : [
                  ...(DRAWOLA_FEATURES.SHOW_DRAWOLA_PLUS
                    ? [ExcalidrawPlusCommand]
                    : []),
                  ...(DRAWOLA_FEATURES.SHOW_SIGN_UP
                    ? [ExcalidrawPlusAppCommand]
                    : []),
                ]),
            // DRAWOLA:CUSTOM-END
            {
              label: t("labels.installPWA"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!pwaEvent,
              perform: () => {
                if (pwaEvent) {
                  pwaEvent.prompt();
                  pwaEvent.userChoice.then(() => {
                    // event cannot be reused, but we'll hopefully
                    // grab new one as the event should be fired again
                    pwaEvent = null;
                  });
                }
              },
            },
          ]}
        />
        {isVisualDebuggerEnabled() && excalidrawAPI && (
          <DebugCanvas
            appState={excalidrawAPI.getAppState()}
            scale={window.devicePixelRatio}
            ref={debugCanvasRef}
          />
        )}
        {/* DRAWOLA:CUSTOM-START ENABLE_MULTI_FILE_STORE */}
        {/* Render inside <Excalidraw> so useDrawolaT() -> useI18n() works. */}
        {DRAWOLA_FEATURES.ENABLE_MULTI_FILE_STORE && (
          <BlockingQuotaModal
            result={multiFile.quotaError}
            onDismiss={multiFile.dismissQuotaError}
          />
        )}
        {/* DRAWOLA:CUSTOM-END */}
        {/* DRAWOLA:CUSTOM-START SHOW_MINIMAP */}
        {DRAWOLA_FEATURES.SHOW_MINIMAP && excalidrawAPI && (
          <Minimap api={excalidrawAPI} />
        )}
        {/* DRAWOLA:CUSTOM-END */}
        {/* DRAWOLA:CUSTOM-START SHAPE_LIBRARY */}
        {excalidrawAPI && <DrawolaShapeLibrary api={excalidrawAPI} />}
        {/* DRAWOLA:CUSTOM-END */}
      </Excalidraw>
      {/* DRAWOLA:CUSTOM-START SHOW_MINDMAP */}
      {DRAWOLA_FEATURES.SHOW_MINDMAP && (
        <DrawolaMindmapOverlay excalidrawAPI={excalidrawAPI} />
      )}
      {/* DRAWOLA:CUSTOM-END */}
      {/* DRAWOLA:CUSTOM-START SHOW_STUDIO_HOME */}
      {/* Breadcrumb: same overlay-sibling treatment as the Studio (it needs
          the app jotai store for i18n, not the editor's isolation). Rendered
          only while the editor is the visible view — that is how it "hides
          when the Studio is open". */}
      {studioEnabled && !isStudioOpen && (
        <EditorBreadcrumb
          theme={editorTheme}
          excalidrawAPI={excalidrawAPI}
          activeDocId={multiFile.activeDocId}
          refreshToken={multiFile.refreshToken}
          // shown while collaborating too: `handleGoHome` leaves the room
          // first, and hiding it left anyone who joined by link with no way
          // out of the session at all
          onGoHome={handleGoHome}
          onDocRenamed={multiFile.notifyDocsChanged}
          // DRAWOLA:CUSTOM-START SHOW_CLOUD_STORAGE sync
          cloudSync={cloudSync ?? undefined}
          // DRAWOLA:CUSTOM-END
        />
      )}
      {/* Sibling of <Excalidraw>, not a child: the Studio is a full-screen
          overlay that must not inherit the editor's jotai isolation. The
          editor below stays mounted so `useMultiFileStore` keeps its boot
          state, pending debounced save and switch logic. */}
      {studioEnabled && isStudioOpen && (
        <DrawolaStudio
          theme={editorTheme}
          ready={multiFile.booted}
          refreshToken={multiFile.refreshToken}
          onOpenDoc={async (docId) => {
            // DRAWOLA:CUSTOM-START SHOW_CLOUD_STORAGE sync
            // pull before the switch, so the canvas never shows a stale copy
            await cloudSyncEngine?.pullIfRemoteNewer(docId);
            // DRAWOLA:CUSTOM-END
            // flushes the pending save for the previous draw, then replaces
            // the editor scene with the target doc
            await multiFile.switchTo(docId);
            showEditor();
          }}
          onOpenCreatedDoc={async (docId) => {
            await multiFile.onCreated(docId);
            showEditor();
          }}
          onActiveDocDeleted={() => multiFile.onActiveDeleted()}
          // DRAWOLA:CUSTOM-START SHOW_SHARE_LINK
          onShareDoc={
            DRAWOLA_FEATURES.SHOW_SHARE_LINK ? handleShareDoc : undefined
          }
          onRevokeShare={
            DRAWOLA_FEATURES.SHOW_SHARE_LINK ? handleRevokeShare : undefined
          }
          // DRAWOLA:CUSTOM-END
          // DRAWOLA:CUSTOM-START SHOW_CLOUD_STORAGE sync
          cloudSync={cloudSync ?? undefined}
          // DRAWOLA:CUSTOM-END
        />
      )}
      {/* DRAWOLA:CUSTOM-START SHOW_CLOUD_STORAGE sync */}
      {/* One panel for both views: the Studio card and the breadcrumb open the
          same `repairDocId`, so it renders outside the Studio conditional. */}
      {cloudSync && cloudSync.repairDocId && (
        <SyncRepairPanel cloudSync={cloudSync} theme={editorTheme} />
      )}
      {/* DRAWOLA:CUSTOM-END */}
      {/* DRAWOLA:CUSTOM-END */}
    </div>
  );
};

const DrawolaApp = () => {
  const isCloudExportWindow =
    window.location.pathname === "/excalidraw-plus-export";
  if (isCloudExportWindow) {
    return <ExcalidrawPlusIframeExport />;
  }

  return (
    <TopErrorBoundary>
      <Provider store={appJotaiStore}>
        <ExcalidrawAPIProvider>
          <ExcalidrawWrapper />
        </ExcalidrawAPIProvider>
      </Provider>
    </TopErrorBoundary>
  );
};

export default DrawolaApp;
